<template>
  <div class="rb">
    <!-- ══════════ Sticky toolbar ══════════ -->
    <header class="rb-toolbar">
      <div class="rb-toolbar-id">
        <div class="rb-logo">
          <feather-icon icon="LayersIcon" size="18" />
        </div>
        <div class="rb-id-text">
          <div class="rb-eyebrow">Report Builder</div>
          <div class="rb-name-row">
            <input
              v-model="reportName"
              class="rb-name"
              placeholder="Untitled report"
              aria-label="Report name"
            />
            <span class="rb-type-tag">Rights Avails</span>
          </div>
        </div>
      </div>

      <!-- Workflow steps -->
      <nav class="rb-steps" aria-label="Report workflow">
        <button
          v-for="step in steps"
          :key="step.id"
          type="button"
          class="rb-step"
          :class="{ done: step.done, active: step.active }"
          @click="goToStep(step.id)"
        >
          <span class="rb-step-dot">
            <feather-icon v-if="step.done" icon="CheckIcon" size="11" />
            <template v-else>{{ step.n }}</template>
          </span>
          <span class="rb-step-label">{{ step.label }}</span>
        </button>
      </nav>

      <div class="rb-toolbar-actions">
        <b-dropdown
          size="sm"
          variant="outline-secondary"
          right
          no-caret
          toggle-class="rb-icon-btn"
          menu-class="rb-menu"
        >
          <template #button-content>
            <feather-icon icon="BookmarkIcon" size="14" class="mr-50" />
            Views
          </template>
          <b-dropdown-header class="rb-menu-header">Team views</b-dropdown-header>
          <b-dropdown-item v-if="!savedViews.length" disabled>
            No saved views yet
          </b-dropdown-item>
          <b-dropdown-item
            v-for="view in savedViews"
            :key="view.id"
            @click="applyView(view)"
          >
            <div class="rb-menu-item">
              <span class="rb-menu-item-name">
                {{ view.name }}
                <small v-if="!view.isMine" class="rb-menu-item-owner">
                  by {{ view.createdByName || 'a teammate' }}
                </small>
              </span>
              <span
                v-if="view.isMine"
                class="rb-menu-item-del"
                title="Delete view"
                @click.stop="deleteView(view)"
              >
                <feather-icon icon="Trash2Icon" size="13" />
              </span>
            </div>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="saveCurrentView">
            <feather-icon icon="PlusIcon" size="13" class="mr-50" />
            Save current setup
          </b-dropdown-item>
        </b-dropdown>

        <b-button
          variant="outline-secondary"
          size="sm"
          class="rb-btn"
          :disabled="queueing"
          @click="resetAll"
        >
          <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
          Reset
        </b-button>

        <b-button
          variant="primary"
          size="sm"
          class="rb-btn rb-btn--run"
          :disabled="!isReady || queueing"
          title="Builds in the background — you don't have to wait here"
          @click="queueExport"
        >
          <b-spinner v-if="queueing" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
          Queue export
        </b-button>
      </div>
    </header>

    <!-- ══════════ Readiness strip ══════════ -->
    <div v-if="!isReady" class="rb-notice">
      <feather-icon icon="InfoIcon" size="14" />
      <span>{{ readinessMessage }}</span>
    </div>

    <!-- ══════════ Filters ══════════ -->
    <section ref="filtersSection" class="rb-card">
      <header class="rb-card-head">
        <button type="button" class="rb-card-toggle" @click="filtersOpen = !filtersOpen">
          <feather-icon :icon="filtersOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="16" />
          <feather-icon icon="FilterIcon" size="15" />
          <span class="rb-card-title">Filters</span>
          <span v-if="activeFilterChips.length" class="rb-count">
            {{ activeFilterChips.length }}
          </span>
        </button>

        <div class="rb-card-head-right">
          <button
            v-if="activeFilterChips.length"
            type="button"
            class="rb-link-btn"
            @click="clearFilters"
          >
            Clear all
          </button>
        </div>
      </header>

      <!-- Collapsed summary -->
      <div v-if="!filtersOpen && activeFilterChips.length" class="rb-chip-row">
        <button
          v-for="chip in activeFilterChips"
          :key="chip.key"
          type="button"
          class="rb-chip rb-chip--removable"
          @click="removeFilterChip(chip)"
        >
          <span class="rb-chip-label">{{ chip.label }}</span>
          <feather-icon icon="XIcon" size="11" />
        </button>
      </div>

      <div v-show="filtersOpen" class="rb-card-body">
        <!-- Date window -->
        <div class="rb-subsection">
          <div class="rb-subsection-head">
            <feather-icon icon="CalendarIcon" size="13" />
            <span>Availability window</span>
            <span class="rb-req">Required</span>
          </div>

          <div class="rb-preset-row">
            <button
              v-for="preset in datePresets"
              :key="preset.key"
              type="button"
              class="rb-preset"
              :class="{ active: activePreset === preset.key }"
              @click="applyDatePreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>

          <div class="rb-grid rb-grid--dates">
            <div class="rb-field">
              <label class="rb-label">From</label>
              <flat-pickr
                v-model="filters.start_date"
                :config="{ ...$flatPickrConfig }"
                class="form-control rb-input"
                placeholder="Select start date"
              />
            </div>
            <div class="rb-field">
              <label class="rb-label">To</label>
              <flat-pickr
                v-model="filters.end_date"
                :config="{ ...$flatPickrConfig }"
                class="form-control rb-input"
                placeholder="Select end date"
              />
            </div>
            <div class="rb-field rb-field--span2 rb-window-note">
              <feather-icon icon="ShieldIcon" size="13" />
              <span>
                A bounded window keeps queries fast across large catalogues.
                <template v-if="windowLengthLabel"> Current span: <strong>{{ windowLengthLabel }}</strong>.</template>
              </span>
            </div>
          </div>
        </div>

        <!-- Scope -->
        <div class="rb-subsection">
          <div class="rb-subsection-head">
            <feather-icon icon="CropIcon" size="13" />
            <span>Scope</span>
          </div>

          <div class="rb-grid">
            <div class="rb-field rb-field--span2">
              <label class="rb-label">Movies</label>
              <v-select
                v-model="filtersUi.movies"
                :options="movieOptions"
                :filterable="false"
                label="name"
                multiple
                :close-on-select="false"
                placeholder="All movies — type to search"
                class="rb-select"
                @search="onMovieSearch"
              />
            </div>
            <div class="rb-field rb-field--span2">
              <label class="rb-label">Language</label>
              <v-select
                v-model="filtersUi.languages"
                :options="options.languages"
                label="name"
                multiple
                :close-on-select="false"
                placeholder="All languages"
                class="rb-select"
              />
            </div>
            <div class="rb-field rb-field--span2">
              <label class="rb-label">
                Territory
                <span class="rb-label-hint">groups · territories · sub-territories</span>
              </label>
              <master-treeselect
                :key="'territory-' + treeKey"
                :allowSelectingDisabledDescendants="true"
                :options="territoryTreeMain"
                multiple
                placeholder="All territories"
                :value="filters.territory"
                @input="onTerritoryInput"
              />
            </div>
            <div class="rb-field rb-field--span2">
              <label class="rb-label">
                Right
                <span class="rb-label-hint">parents expand to sub-rights</span>
              </label>
              <master-treeselect
                :key="'right-' + treeKey"
                :allowSelectingDisabledDescendants="true"
                :options="rightTreeMain"
                multiple
                placeholder="All rights"
                :value="filters.right"
                @input="onRightInput"
              />
            </div>
          </div>
        </div>

        <!-- Availability rules -->
        <div class="rb-subsection">
          <div class="rb-subsection-head">
            <feather-icon icon="SlidersIcon" size="13" />
            <span>Availability rules</span>
          </div>

          <div class="rb-grid">
            <div class="rb-field rb-field--span4">
              <label class="rb-label">
                Show rights that are
                <span class="rb-label-hint">within the selected window</span>
              </label>
              <div class="rb-optgrid">
                <button
                  v-for="opt in availabilityOptions"
                  :key="opt.key"
                  type="button"
                  class="rb-opt"
                  :class="{ active: filters.availability === opt.key }"
                  @click="filters.availability = opt.key"
                >
                  <span class="rb-opt-name">{{ opt.name }}</span>
                  <span class="rb-opt-desc">{{ opt.description }}</span>
                </button>
              </div>
            </div>

            <div class="rb-field rb-field--span2">
              <label class="rb-label">Acquired as</label>
              <div class="rb-segmented">
                <button
                  v-for="opt in acquiredAsOptions"
                  :key="opt.key"
                  type="button"
                  class="rb-seg"
                  :class="{ active: filters.acquired_as === opt.key }"
                  @click="filters.acquired_as = opt.key"
                >
                  {{ opt.name }}
                </button>
              </div>
              <p class="rb-help">{{ acquiredAsHelp }}</p>
            </div>

            <div class="rb-field rb-field--span2">
              <label class="rb-label">Holdbacks</label>
              <div class="rb-segmented">
                <button
                  v-for="opt in holdbackOptions"
                  :key="String(opt.id)"
                  type="button"
                  class="rb-seg"
                  :class="{ active: holdbackValue === opt.id }"
                  @click="setHoldbacks(opt.id)"
                >
                  {{ opt.name }}
                </button>
              </div>
              <p class="rb-help">{{ holdbackHelp }}</p>
            </div>
          </div>

          <p v-if="combinationHelp" class="rb-combo-note">
            <feather-icon icon="TargetIcon" size="13" />
            <span>{{ combinationHelp }}</span>
          </p>

          <div v-show="showHoldbackFilters" class="rb-grid rb-nested">
            <div class="rb-field rb-field--span2">
              <label class="rb-label">Holdback territory</label>
              <master-treeselect
                :key="'hb-territory-' + treeKey"
                :allowSelectingDisabledDescendants="true"
                :options="territoryTreeHoldback"
                multiple
                placeholder="Any territory"
                :value="filters.holdback_territory"
                @input="onHoldbackTerritoryInput"
              />
            </div>
            <div class="rb-field rb-field--span2">
              <label class="rb-label">Holdback right</label>
              <master-treeselect
                :key="'hb-right-' + treeKey"
                :allowSelectingDisabledDescendants="true"
                :options="rightTreeHoldback"
                multiple
                placeholder="Any right"
                :value="filters.holdback_right"
                @input="onHoldbackRightInput"
              />
            </div>
            <div class="rb-field">
              <label class="rb-label">Holdback start</label>
              <flat-pickr
                v-model="filters.holdback_start_date"
                :config="{ ...$flatPickrConfig }"
                class="form-control rb-input"
                placeholder="Any"
              />
            </div>
            <div class="rb-field">
              <label class="rb-label">Holdback end</label>
              <flat-pickr
                v-model="filters.holdback_end_date"
                :config="{ ...$flatPickrConfig }"
                class="form-control rb-input"
                placeholder="Any"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════ Column builder ══════════ -->
    <section ref="builderSection" class="rb-builder">
      <!-- Library -->
      <div class="rb-panel">
        <header class="rb-panel-head">
          <div class="rb-panel-head-main">
            <h2 class="rb-panel-title">
              <feather-icon icon="GridIcon" size="15" />
              Field library
              <span class="rb-count">{{ filteredAvailable.length }}</span>
            </h2>
            <p class="rb-panel-hint">Click a field or drag it into the layout</p>
          </div>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="rb-mini-btn"
            :disabled="!filteredAvailable.length"
            @click="addAllVisible"
          >
            Add all
          </b-button>
        </header>

        <div class="rb-panel-tools">
          <div class="rb-search">
            <feather-icon icon="SearchIcon" size="14" class="rb-search-icon" />
            <input
              v-model="columnSearch"
              type="text"
              class="rb-search-input"
              placeholder="Search fields…"
              aria-label="Search fields"
            />
            <button
              v-if="columnSearch"
              type="button"
              class="rb-search-clear"
              @click="columnSearch = ''"
            >
              <feather-icon icon="XIcon" size="13" />
            </button>
          </div>

          <div class="rb-tabs">
            <button
              type="button"
              class="rb-tab"
              :class="{ active: activeGroup === 'all' }"
              @click="activeGroup = 'all'"
            >
              All <span>{{ availableColumns.length }}</span>
            </button>
            <button
              v-for="g in columnGroups"
              :key="g.name"
              type="button"
              class="rb-tab"
              :class="{ active: activeGroup === g.name }"
              @click="activeGroup = g.name"
            >
              {{ g.name }} <span>{{ g.count }}</span>
            </button>
          </div>
        </div>

        <div class="rb-scroll">
          <draggable
            v-model="filteredAvailableProxy"
            v-bind="dragOptions"
            class="rb-list"
            :group="{ name: 'rb-cols', pull: true, put: true }"
            @start="dragging = true"
            @end="onDragEnd"
          >
            <div
              v-for="col in filteredAvailable"
              :key="'avail-' + col.key"
              class="rb-item"
              role="button"
              tabindex="0"
              :title="fieldTitle(col)"
              @click="addColumn(col)"
              @keydown.enter.prevent="addColumn(col)"
            >
              <span class="rb-item-icon" :class="groupClass(col.group)">
                <feather-icon :icon="groupIcon(col.group)" size="12" />
              </span>
              <span class="rb-item-name">{{ col.name }}</span>
              <span class="rb-item-action"><feather-icon icon="PlusIcon" size="14" /></span>
            </div>
          </draggable>

          <div v-if="!filteredAvailable.length" class="rb-blank">
            <feather-icon icon="CheckCircleIcon" size="24" class="mb-1" />
            <p>{{ columnSearch ? 'No fields match your search' : 'Every field is already in the layout' }}</p>
          </div>
        </div>
      </div>

      <!-- Layout -->
      <div class="rb-panel rb-panel--layout">
        <header class="rb-panel-head">
          <div class="rb-panel-head-main">
            <h2 class="rb-panel-title">
              <feather-icon icon="ColumnsIcon" size="15" />
              Report layout
              <span class="rb-count rb-count--solid">{{ outputColumns.length }}</span>
            </h2>
            <p class="rb-panel-hint">Drag to reorder — order matches the exported file</p>
          </div>
          <div class="rb-panel-head-actions">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="rb-mini-btn"
              @click="resetOutputToDefaults"
            >
              Defaults
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              class="rb-mini-btn"
              :disabled="!outputColumns.length"
              @click="clearOutput"
            >
              Clear
            </b-button>
          </div>
        </header>

        <div class="rb-panel-tools rb-panel-tools--showby">
          <div class="rb-showby">
            <span class="rb-showby-label">Group rows by</span>
            <div class="rb-showby-chips">
              <button
                v-for="opt in showByOptions"
                :key="opt.key"
                type="button"
                class="rb-toggle"
                :class="{ active: showBy.includes(opt.key) }"
                @click="toggleShowBy(opt.key)"
              >
                <feather-icon
                  :icon="showBy.includes(opt.key) ? 'CheckIcon' : 'PlusIcon'"
                  size="11"
                  class="mr-25"
                />
                {{ opt.name }}
              </button>
            </div>
          </div>
          <p class="rb-help mb-0">{{ showByHelp }}</p>
        </div>

        <div class="rb-scroll rb-scroll--layout">
          <draggable
            v-model="outputColumns"
            v-bind="dragOptions"
            class="rb-list rb-list--out"
            :group="{ name: 'rb-cols', pull: true, put: true }"
            @start="dragging = true"
            @end="onDragEnd"
          >
            <div
              v-for="(col, index) in outputColumns"
              :key="'out-' + col.key"
              class="rb-item rb-item--out"
              :title="fieldTitle(col)"
            >
              <span class="rb-idx">{{ index + 1 }}</span>
              <span class="rb-item-icon" :class="groupClass(col.group)">
                <feather-icon :icon="groupIcon(col.group)" size="12" />
              </span>
              <span class="rb-item-name">{{ col.name }}</span>
              <button
                type="button"
                class="rb-item-remove"
                :aria-label="'Remove ' + col.name"
                @click.stop="removeColumn(col)"
              >
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>
          </draggable>

          <div v-if="hasImageColumn" class="rb-inline-note">
            <feather-icon icon="AlertCircleIcon" size="13" />
            <span>
              Artwork has to be embedded cell by cell, so exports including it are
              capped at 20,000 rows. Remove it for an uncapped export.
            </span>
          </div>

          <div v-if="!outputColumns.length" class="rb-blank rb-blank--drop">
            <feather-icon icon="MoveIcon" size="26" class="mb-1" />
            <p class="mb-0"><strong>Drop fields here</strong></p>
            <small>Or click any field in the library to add it</small>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════ Exports ══════════ -->
    <section ref="resultsSection" class="rb-card rb-results">
      <header class="rb-results-head">
        <div class="rb-results-title">
          <h2 class="rb-panel-title mb-0">
            <feather-icon icon="DownloadCloudIcon" size="15" />
            Exports
          </h2>
          <span v-if="activeJobCount" class="rb-total">
            {{ activeJobCount }} running
          </span>
        </div>

        <div class="rb-results-tools">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="rb-icon-only"
            title="Refresh"
            :disabled="jobsLoading"
            @click="loadJobs()"
          >
            <feather-icon icon="RefreshCwIcon" size="14" :class="{ 'rb-spin': jobsLoading }" />
          </b-button>
        </div>
      </header>

      <div v-if="summaryChips.length" class="rb-chip-row rb-chip-row--applied">
        <span class="rb-applied-label">Current</span>
        <span v-for="(chip, i) in summaryChips" :key="i" class="rb-chip">{{ chip }}</span>
      </div>

      <div v-if="error" class="rb-state rb-state--error">
        <feather-icon icon="AlertTriangleIcon" size="28" class="mb-1" />
        <p class="mb-2">{{ error }}</p>
      </div>

      <div v-else-if="!jobs.length && !jobsLoading" class="rb-state">
        <div class="rb-state-icon"><feather-icon icon="DownloadCloudIcon" size="26" /></div>
        <h3 class="rb-state-title">No exports yet</h3>
        <p class="rb-state-sub">
          Set your window and fields, then queue an export. It builds in the background —
          you can leave this page and we'll notify you when the file is ready.
        </p>
        <b-button variant="primary" size="sm" :disabled="!isReady || queueing" @click="queueExport">
          <feather-icon icon="PlayIcon" size="14" class="mr-50" />
          Queue export
        </b-button>
      </div>

      <ul v-else class="rb-jobs">
        <li v-for="job in jobs" :key="job.job_id" class="rb-job" :class="'rb-job--' + job.status">
          <span class="rb-job-status" :class="'rb-job-status--' + job.status">
            <feather-icon :icon="jobIcon(job.status)" size="13" :class="{ 'rb-spin': job.status === 'running' }" />
            {{ jobStatusLabel(job.status) }}
          </span>

          <span class="rb-job-name" :title="job.report_name || 'Rights Avails'">
            {{ job.report_name || 'Rights Avails' }}
          </span>
          <span class="rb-job-meta">{{ jobMeta(job) }}</span>
          <span v-if="job.error" class="rb-job-error" :title="job.error">{{ job.error }}</span>

          <div class="rb-job-actions">
            <b-button
              v-if="job.status === 'done' && job.file_name"
              size="sm"
              variant="primary"
              block
              :href="downloadUrl(job)"
              target="_blank"
            >
              <feather-icon icon="DownloadIcon" size="13" class="mr-50" />
              Download
            </b-button>
            <b-button
              v-else-if="job.status === 'queued'"
              size="sm"
              variant="outline-secondary"
              block
              @click="cancelJob(job)"
            >
              Cancel
            </b-button>
            <b-button
              v-else-if="job.status === 'failed'"
              size="sm"
              variant="outline-primary"
              block
              @click="requeueJob(job)"
            >
              Retry
            </b-button>
            <span v-else class="rb-job-wait">Building…</span>
          </div>
        </li>
      </ul>

      <p v-if="truncatedJob" class="rb-job-note">
        <feather-icon icon="AlertTriangleIcon" size="13" class="mr-50" />
        Artwork exports are capped at {{ Number(truncatedJob.row_limit).toLocaleString() }} rows.
        Remove the artwork column for an uncapped export.
      </p>
    </section>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import moment from 'moment'
import { getMasterList, getAllMovies } from '@/apiServices/MastersServices'
import {
  GetRightsAvailsMeta,
  PostRightsAvailsExport,
  GetRightsAvailsExportJobs,
  CancelRightsAvailsExportJob,
  getRightsAvailsDownloadUrl,
  GetRightsAvailsViews,
  SaveRightsAvailsView,
  DeleteRightsAvailsView,
} from '@/apiServices/ReportBuilderV2Services'

// Views used to live only in localStorage, which logout clears; any left there are
// moved to the server once and the key is dropped.
const LEGACY_VIEWS_STORAGE_KEY = 'rb2_rights_avails_views'
const DATE_FMT = 'YYYY-MM-DD'

const emptyFilters = () => ({
  start_date: null,
  end_date: null,
  territory: [],
  right: [],
  availability: 'sellable',
  acquired_as: 'any',
  holdback_territory: [],
  holdback_right: [],
  holdback_start_date: null,
  holdback_end_date: null,
})

const DEFAULT_AVAILABILITY_OPTIONS = [
  { key: 'sellable', name: 'Available', description: 'Excluding exclusive sales. Includes rights sold non-exclusively, which you can still sell again.' },
  { key: 'sold', name: 'Sold', description: 'Any overlapping sale, exclusive or not.' },
  { key: 'sold_exclusive', name: 'Sold exclusive', description: 'Blocked — an exclusive sale overlaps this window.' },
  { key: 'sold_non_exclusive', name: 'Sold non-exclusive', description: 'Sold, but no exclusive sale blocks a further deal.' },
]

const DEFAULT_ACQUIRED_AS_OPTIONS = [
  { key: 'any', name: 'Any' },
  { key: 'exclusive', name: 'Exclusive' },
  { key: 'non_exclusive', name: 'Non-exclusive' },
]

const DEFAULT_SHOW_BY = [
  { key: 'right', name: 'Right' },
  { key: 'territory', name: 'Territory' },
  { key: 'language', name: 'Language' },
]

const DEFAULT_COLUMNS = [
  { key: 'movie_unique', name: 'Movie ID', group: 'Avails', default: true },
  { key: 'movie_title', name: 'Movie', group: 'Avails', default: true },
  { key: 'right_name', name: 'Right', group: 'Avails', default: true },
  { key: 'territory_name', name: 'Territory', group: 'Avails', default: true },
  { key: 'language_name', name: 'Language', group: 'Avails', default: true },
  { key: 'acq_start', name: 'Acquisition From', group: 'Avails', default: true },
  { key: 'acq_end', name: 'Acquisition To', group: 'Avails', default: true },
  { key: 'acq_exclusive', name: 'Acq Exclusive', group: 'Avails', default: true },
  { key: 'availability', name: 'Availability', group: 'Avails', default: true },
  { key: 'sale_exclusive', name: 'Sale Exclusive', group: 'Avails', default: true },
  { key: 'sale_start', name: 'Sale From', group: 'Avails', default: false },
  { key: 'sale_end', name: 'Sale To', group: 'Avails', default: false },
  { key: 'has_holdback', name: 'Holdback', group: 'Avails', default: true },
  { key: 'acq_contract_ids', name: 'Acquisition Contracts', group: 'Avails', default: false },
  { key: 'sale_contract_ids', name: 'Sales Contracts', group: 'Avails', default: false },
  { key: 'international_title', name: 'International Title', group: 'Movie Details', default: false },
  { key: 'year', name: 'Year', group: 'Movie Details', default: false },
  { key: 'duration', name: 'Duration', group: 'Movie Details', default: false },
  { key: 'country', name: 'Production Country', group: 'Movie Details', default: false },
  { key: 'movie_genres', name: 'Genres', group: 'Movie Details', default: false },
  { key: 'movie_types', name: 'Movie Type', group: 'Movie Details', default: false },
  { key: 'movie_age', name: 'Age / Rating', group: 'Movie Details', default: false },
  { key: 'suisan_number', name: 'SUISA Number', group: 'Movie Details', default: false },
  { key: 'isan_number', name: 'ISAN', group: 'Movie Details', default: false },
  { key: 'weblink', name: 'Web Link', group: 'Movie Details', default: false },
  { key: 'trailer_link', name: 'Trailer Link', group: 'Movie Details', default: false },
  { key: 'director', name: 'Director', group: 'Crew', default: false },
  { key: 'cast', name: 'Cast', group: 'Crew', default: false },
  { key: 'poster_file', name: 'Artwork / Poster', group: 'Artwork', default: false, type: 'image' },
]

function deepClone(value) {
  return JSON.parse(JSON.stringify(value || []))
}

function normalizeTreeIds(values) {
  if (!Array.isArray(values)) return []
  return [
    ...new Set(
      values
        .map((v) => {
          if (v && typeof v === 'object') return parseInt(v.lbl_id || v.value || v.id, 10)
          const s = String(v)
          if (s.includes('_group')) return parseInt(s.split('_')[0], 10)
          return parseInt(s, 10)
        })
        .filter((n) => !Number.isNaN(n) && n > 0)
    ),
  ]
}

export default {
  name: 'ReportBuilderV2',
  components: { flatPickr, vSelect, draggable },
  data() {
    return {
      metaLoaded: false,
      queueing: false,
      jobsLoading: false,
      dragging: false,
      filtersOpen: true,
      error: null,
      reportName: 'Rights Avails',
      treeKey: 0,
      columnSearch: '',
      activeGroup: 'all',
      savedViews: [],
      filters: emptyFilters(),
      filtersUi: {
        movies: [],
        languages: [],
        holdbacks: null,
      },
      options: {
        territoryTree: [],
        rightTree: [],
        languages: [],
      },
      territoryTreeMain: [],
      territoryTreeHoldback: [],
      rightTreeMain: [],
      rightTreeHoldback: [],
      movieOptions: [],
      movieSearchTimer: null,
      availabilityOptions: DEFAULT_AVAILABILITY_OPTIONS,
      acquiredAsOptions: DEFAULT_ACQUIRED_AS_OPTIONS,
      holdbackOptions: [
        { id: null, name: 'Any' },
        { id: true, name: 'With holdback' },
        { id: false, name: 'Without holdback' },
      ],
      showByOptions: DEFAULT_SHOW_BY,
      columnOptions: DEFAULT_COLUMNS,
      availableColumns: [],
      outputColumns: [],
      showBy: ['right', 'territory', 'language'],
      jobs: [],
      jobsTimer: null,
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 170,
        ghostClass: 'rb-ghost',
        chosenClass: 'rb-chosen',
        dragClass: 'rb-dragging',
        disabled: false,
      }
    },
    hasDates() {
      return Boolean(this.filters.start_date && this.filters.end_date)
    },
    isReady() {
      return this.hasDates && this.outputColumns.length > 0
    },
    readinessMessage() {
      if (!this.hasDates && !this.outputColumns.length) {
        return 'Set an availability window and add at least one field to run this report.'
      }
      if (!this.hasDates) return 'Set a From and To date to run this report.'
      return 'Add at least one field to the report layout.'
    },
    steps() {
      return [
        { id: 'filters', n: 1, label: 'Filters', done: this.hasDates, active: !this.hasDates },
        {
          id: 'builder',
          n: 2,
          label: 'Fields',
          done: this.outputColumns.length > 0,
          active: this.hasDates && !this.outputColumns.length,
        },
        {
          id: 'results',
          n: 3,
          label: 'Export',
          done: this.jobs.some((j) => j.status === 'done'),
          active: this.isReady,
        },
      ]
    },
    datePresets() {
      const fwd = (months) => () => [
        moment().format(DATE_FMT),
        moment().add(months, 'months').format(DATE_FMT),
      ]
      const year = (offset) => () => [
        moment().add(offset, 'year').startOf('year').format(DATE_FMT),
        moment().add(offset, 'year').endOf('year').format(DATE_FMT),
      ]
      return [
        { key: 'next6', label: 'Next 6 months', range: fwd(6) },
        { key: 'next12', label: 'Next 12 months', range: fwd(12) },
        { key: 'next24', label: 'Next 24 months', range: fwd(24) },
        { key: 'thisYear', label: 'This year', range: year(0) },
        { key: 'nextYear', label: 'Next year', range: year(1) },
      ]
    },
    activePreset() {
      if (!this.hasDates) return null
      const match = this.datePresets.find((p) => {
        const [start, end] = p.range()
        return start === this.filters.start_date && end === this.filters.end_date
      })
      return match ? match.key : null
    },
    windowLengthLabel() {
      if (!this.hasDates) return ''
      const start = moment(this.filters.start_date)
      const end = moment(this.filters.end_date)
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) return ''
      const months = end.diff(start, 'months')
      const days = end.diff(start, 'days')
      if (months >= 1) return `${months} month${months === 1 ? '' : 's'}`
      return `${days} day${days === 1 ? '' : 's'}`
    },
    activeJobCount() {
      return this.jobs.filter((j) => j.status === 'queued' || j.status === 'running').length
    },
    truncatedJob() {
      return this.jobs.find((j) => j.status === 'done' && j.truncated) || null
    },
    holdbackValue() {
      return this.filtersUi.holdbacks == null ? null : this.filtersUi.holdbacks.id
    },
    availabilityName() {
      const match = this.availabilityOptions.find((o) => o.key === this.filters.availability)
      return match ? match.name : 'Available'
    },
    acquiredAsName() {
      const match = this.acquiredAsOptions.find((o) => o.key === this.filters.acquired_as)
      return match ? match.name : 'Any'
    },
    acquiredAsHelp() {
      if (this.filters.acquired_as === 'exclusive') {
        return 'Only rights we hold exclusively — the ones we can resell exclusively.'
      }
      if (this.filters.acquired_as === 'non_exclusive') {
        return 'Only rights we hold non-exclusively.'
      }
      return 'Both exclusive and non-exclusive acquisitions.'
    },
    combinationHelp() {
      const sale = this.filters.availability
      const acq = this.filters.acquired_as
      if (acq === 'exclusive' && sale === 'sellable') {
        return 'Held exclusively and not blocked by an exclusive sale — your premium open inventory.'
      }
      if (acq === 'non_exclusive' && sale === 'sellable') {
        return 'Shared rights that are still open, so only non-exclusive deals are possible.'
      }
      if (sale === 'sold_exclusive') {
        return 'These are blocked for the window. Use this to audit what is already committed.'
      }
      if (sale === 'sold_non_exclusive') {
        return 'Already sold, but not exclusively — these can still be sold again.'
      }
      return ''
    },
    holdbackHelp() {
      if (this.holdbackValue === true) return 'Only rights covered by a holdback.'
      if (this.holdbackValue === false) return 'Excludes rights covered by a holdback.'
      return 'Holdbacks are flagged but not filtered.'
    },
    showByHelp() {
      const names = this.showByOptions
        .filter((o) => this.showBy.includes(o.key))
        .map((o) => o.name.toLowerCase())
      if (names.length === 3) return 'One row per movie × right × territory × language.'
      if (!names.length) return 'Select at least one dimension.'
      return `Rows collapse to movie × ${names.join(' × ')}; other dimensions are merged into a list.`
    },
    showHoldbackFilters() {
      return this.holdbackValue !== null
    },
    selectedColumns() {
      return this.outputColumns.map((c) => c.key)
    },
    columnGroups() {
      const counts = {}
      for (const col of this.availableColumns) {
        const name = col.group || 'Avails'
        counts[name] = (counts[name] || 0) + 1
      }
      return Object.keys(counts).map((name) => ({ name, count: counts[name] }))
    },
    filteredAvailable() {
      const q = this.columnSearch.trim().toLowerCase()
      return this.availableColumns.filter((c) => {
        if (this.activeGroup !== 'all' && (c.group || 'Avails') !== this.activeGroup) return false
        if (!q) return true
        return (
          String(c.name || '').toLowerCase().includes(q) ||
          String(c.group || '').toLowerCase().includes(q)
        )
      })
    },
    filteredAvailableProxy: {
      get() {
        return this.filteredAvailable
      },
      set(value) {
        const visibleKeys = new Set(this.filteredAvailable.map((c) => c.key))
        const hidden = this.availableColumns.filter((c) => !visibleKeys.has(c.key))
        const incoming = Array.isArray(value) ? value : []
        const seen = new Set()
        const next = []
        for (const col of [...incoming, ...hidden]) {
          if (!col || seen.has(col.key)) continue
          if (this.outputColumns.some((o) => o.key === col.key)) continue
          seen.add(col.key)
          next.push(col)
        }
        this.availableColumns = next
      },
    },
    activeFilterChips() {
      const chips = []
      if (this.filters.start_date) {
        chips.push({ key: 'start_date', label: `From ${this.filters.start_date}` })
      }
      if (this.filters.end_date) {
        chips.push({ key: 'end_date', label: `To ${this.filters.end_date}` })
      }
      if (this.filtersUi.movies.length) {
        chips.push({ key: 'movies', label: `${this.filtersUi.movies.length} movie(s)` })
      }
      if (this.filters.territory.length) {
        chips.push({ key: 'territory', label: `${this.filters.territory.length} territory` })
      }
      if (this.filters.right.length) {
        chips.push({ key: 'right', label: `${this.filters.right.length} right(s)` })
      }
      if (this.filtersUi.languages.length) {
        chips.push({ key: 'languages', label: `${this.filtersUi.languages.length} language(s)` })
      }
      chips.push({ key: 'availability', label: this.availabilityName })
      if (this.filters.acquired_as !== 'any') {
        chips.push({ key: 'acquired_as', label: `Acquired ${this.acquiredAsName.toLowerCase()}` })
      }
      if (this.holdbackValue !== null) {
        chips.push({ key: 'holdbacks', label: this.filtersUi.holdbacks.name })
      }
      return chips
    },
    summaryChips() {
      if (!this.hasDates) return []
      const chips = [`${this.filters.start_date} → ${this.filters.end_date}`]
      chips.push(this.availabilityName)
      if (this.filters.acquired_as !== 'any') {
        chips.push(`Acquired ${this.acquiredAsName.toLowerCase()}`)
      }
      if (this.filtersUi.movies.length) chips.push(`${this.filtersUi.movies.length} movies`)
      if (this.filters.territory.length) chips.push(`${this.filters.territory.length} territories`)
      if (this.filters.right.length) chips.push(`${this.filters.right.length} rights`)
      if (this.filtersUi.languages.length) {
        chips.push(`${this.filtersUi.languages.length} languages`)
      }
      chips.push(`${this.outputColumns.length} columns`)
      return chips
    },
    hasImageColumn() {
      return this.outputColumns.some((c) => c.key === 'poster_file')
    },
  },
  watch: {
    outputColumns() {
      const outKeys = new Set(this.outputColumns.map((c) => c.key))
      this.availableColumns = this.columnOptions.filter((c) => !outKeys.has(c.key))
    },
  },
  async created() {
    this.initColumnPools(DEFAULT_COLUMNS.filter((c) => c.default).map((c) => c.key))
    this.loadSavedViews()
    await Promise.all([this.loadMeta(), this.loadMasterOptions(), this.loadMovies('')])
    this.loadJobs()
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    if (this.movieSearchTimer) clearTimeout(this.movieSearchTimer)
    this.stopJobPolling()
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && this.isReady && !this.queueing) {
        e.preventDefault()
        this.queueExport()
      }
    },
    goToStep(id) {
      const refName = { filters: 'filtersSection', builder: 'builderSection', results: 'resultsSection' }[id]
      const el = this.$refs[refName]
      if (id === 'filters') this.filtersOpen = true
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    applyDatePreset(preset) {
      const [start, end] = preset.range()
      this.filters.start_date = start
      this.filters.end_date = end
    },
    setHoldbacks(id) {
      this.filtersUi.holdbacks = id === null ? null : this.holdbackOptions.find((o) => o.id === id)
    },
    removeFilterChip(chip) {
      switch (chip.key) {
        case 'start_date':
          this.filters.start_date = null
          break
        case 'end_date':
          this.filters.end_date = null
          break
        case 'movies':
          this.filtersUi.movies = []
          break
        case 'languages':
          this.filtersUi.languages = []
          break
        case 'territory':
          this.filters.territory = []
          this.treeKey += 1
          break
        case 'right':
          this.filters.right = []
          this.treeKey += 1
          break
        case 'availability':
          this.filters.availability = 'sellable'
          break
        case 'acquired_as':
          this.filters.acquired_as = 'any'
          break
        case 'holdbacks':
          this.filtersUi.holdbacks = null
          break
        default:
          break
      }
    },
    clearFilters() {
      this.filters = emptyFilters()
      this.filtersUi = { movies: [], languages: [], holdbacks: null }
      this.treeKey += 1
    },
    fieldTitle(col) {
      if (!col) return ''
      return `${col.name} · ${col.group || 'Avails'}`
    },
    groupIcon(group) {
      if (group === 'Movie Details') return 'FilmIcon'
      if (group === 'Crew') return 'UsersIcon'
      if (group === 'Artwork') return 'ImageIcon'
      return 'ShieldIcon'
    },
    groupClass(group) {
      if (group === 'Movie Details') return 'is-movie'
      if (group === 'Crew') return 'is-crew'
      if (group === 'Artwork') return 'is-art'
      return 'is-avails'
    },
    initColumnPools(selectedKeys) {
      const keys = Array.isArray(selectedKeys) ? selectedKeys : []
      const keySet = new Set(keys)
      this.outputColumns = keys
        .map((key) => this.columnOptions.find((c) => c.key === key))
        .filter(Boolean)
        .map((c) => ({ ...c }))
      this.availableColumns = this.columnOptions
        .filter((c) => !keySet.has(c.key))
        .map((c) => ({ ...c }))
    },
    toggleShowBy(key) {
      if (this.showBy.includes(key)) {
        if (this.showBy.length === 1) return
        this.showBy = this.showBy.filter((k) => k !== key)
      } else {
        this.showBy = [...this.showBy, key]
      }
    },
    addColumn(col) {
      if (!col || this.outputColumns.some((c) => c.key === col.key)) return
      this.outputColumns = [...this.outputColumns, { ...col }]
    },
    addAllVisible() {
      const existing = new Set(this.outputColumns.map((c) => c.key))
      const additions = this.filteredAvailable
        .filter((c) => !existing.has(c.key))
        .map((c) => ({ ...c }))
      this.outputColumns = [...this.outputColumns, ...additions]
    },
    removeColumn(col) {
      this.outputColumns = this.outputColumns.filter((c) => c.key !== col.key)
    },
    clearOutput() {
      this.outputColumns = []
    },
    resetOutputToDefaults() {
      const defaults = this.columnOptions.filter((c) => c.default).map((c) => c.key)
      this.initColumnPools(
        defaults.length ? defaults : this.columnOptions.slice(0, 8).map((c) => c.key)
      )
    },
    onDragEnd() {
      this.dragging = false
      const seen = new Set()
      this.outputColumns = this.outputColumns.filter((c) => {
        if (!c || seen.has(c.key)) return false
        seen.add(c.key)
        return true
      })
    },
    async loadSavedViews() {
      try {
        await this.migrateLegacyViews()
        const res = await GetRightsAvailsViews()
        if (res.data?.status) this.savedViews = res.data.data || []
      } catch (e) {
        this.savedViews = []
      }
    },
    async migrateLegacyViews() {
      let legacy = []
      try {
        legacy = JSON.parse(localStorage.getItem(LEGACY_VIEWS_STORAGE_KEY) || '[]')
      } catch (e) {
        legacy = []
      }
      if (!Array.isArray(legacy) || !legacy.length) return
      try {
        // Oldest first so the newest ends up most recently updated.
        for (const view of [...legacy].reverse()) {
          const { id, ...setup } = view || {}
          if (!setup.name) continue
          // eslint-disable-next-line no-await-in-loop
          await SaveRightsAvailsView(setup)
        }
        localStorage.removeItem(LEGACY_VIEWS_STORAGE_KEY)
      } catch (e) {
        // leave the key so the next visit retries
      }
    },
    buildViewSetup(name) {
      const pick = (list) => (list || []).map((o) => ({ id: o.id, name: o.name }))
      return {
        name,
        // A preset is saved as the preset itself, so "Next 6 months" re-resolves
        // against the day the view is opened rather than the day it was saved.
        datePreset: this.activePreset,
        filters: JSON.parse(JSON.stringify(this.filters)),
        filtersUi: {
          movies: pick(this.filtersUi.movies),
          languages: pick(this.filtersUi.languages),
          holdbacks: this.filtersUi.holdbacks,
        },
        showBy: [...this.showBy],
        columns: this.selectedColumns,
      }
    },
    async saveCurrentView() {
      const name = (this.reportName || '').trim() || `View ${this.savedViews.length + 1}`
      try {
        const res = await SaveRightsAvailsView(this.buildViewSetup(name))
        if (!res.data?.status) throw new Error(res.data?.message || 'Could not save view')
        const saved = res.data.data
        this.reportName = saved.name
        this.savedViews = [saved, ...this.savedViews.filter((v) => v.id !== saved.id)]
        this.$bvToast.toast(`Saved view “${saved.name}”`, {
          title: 'Rights Avails',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message || e.message || 'Could not save view', {
          title: 'Rights Avails',
          variant: 'danger',
          solid: true,
        })
      }
    },
    applyView(view) {
      this.reportName = view.name
      this.filters = { ...emptyFilters(), ...view.filters }
      const preset = view.datePreset && this.datePresets.find((p) => p.key === view.datePreset)
      if (preset) this.applyDatePreset(preset)
      this.filtersUi = { movies: [], languages: [], holdbacks: null, ...view.filtersUi }
      this.showBy = view.showBy?.length ? [...view.showBy] : ['right', 'territory', 'language']
      this.initColumnPools(view.columns || [])
      this.treeKey += 1
    },
    async deleteView(view) {
      try {
        const res = await DeleteRightsAvailsView(view.id)
        if (!res.data?.status) throw new Error(res.data?.message || 'Could not delete view')
        this.savedViews = this.savedViews.filter((v) => v.id !== view.id)
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message || e.message || 'Could not delete view', {
          title: 'Rights Avails',
          variant: 'danger',
          solid: true,
        })
      }
    },
    onTerritoryInput(v) {
      this.filters.territory = normalizeTreeIds(v)
    },
    onRightInput(v) {
      this.filters.right = normalizeTreeIds(v)
    },
    onHoldbackTerritoryInput(v) {
      this.filters.holdback_territory = normalizeTreeIds(v)
    },
    onHoldbackRightInput(v) {
      this.filters.holdback_right = normalizeTreeIds(v)
    },
    async loadMeta() {
      try {
        const res = await GetRightsAvailsMeta()
        if (!res.data?.status) return
        const data = res.data.data
        this.showByOptions = data.show_by || DEFAULT_SHOW_BY
        this.columnOptions = data.columns && data.columns.length ? data.columns : DEFAULT_COLUMNS
        if (data.availability_options?.length) {
          this.availabilityOptions = data.availability_options
        }
        if (data.acquired_as_options?.length) {
          this.acquiredAsOptions = data.acquired_as_options
        }
        this.filters.availability = data.defaults?.availability || 'sellable'
        this.filters.acquired_as = data.defaults?.acquired_as || 'any'
        this.showBy = data.defaults?.show_by || ['right', 'territory', 'language']
        const defaults =
          data.defaults?.columns ||
          this.columnOptions.filter((c) => c.default).map((c) => c.key)
        this.initColumnPools(defaults)
        this.metaLoaded = true
      } catch (e) {
        this.error = 'Failed to load Rights Avails configuration'
      }
    },
    async loadMasterOptions() {
      try {
        const [territory, right, language] = await Promise.all([
          getMasterList({ list_route: 'territory-tree' }),
          getMasterList({ list_route: 'right-tree' }),
          getMasterList({ list_route: 'language' }),
        ])
        if (territory.data?.status) {
          this.options.territoryTree = territory.data.data || []
          this.territoryTreeMain = deepClone(this.options.territoryTree)
          this.territoryTreeHoldback = deepClone(this.options.territoryTree)
        }
        if (right.data?.status) {
          this.options.rightTree = right.data.data || []
          this.rightTreeMain = deepClone(this.options.rightTree)
          this.rightTreeHoldback = deepClone(this.options.rightTree)
        }
        if (language.data?.status) this.options.languages = language.data.data || []
        this.treeKey += 1
      } catch (e) {
        // non-fatal
      }
    },
    async loadMovies(search) {
      try {
        const res = await getAllMovies({ search, limit: 40, archived: false, draft: false })
        if (res.data?.status) this.movieOptions = res.data.data || []
      } catch (e) {
        // ignore
      }
    },
    onMovieSearch(search, loading) {
      if (this.movieSearchTimer) clearTimeout(this.movieSearchTimer)
      loading(true)
      this.movieSearchTimer = setTimeout(async () => {
        await this.loadMovies(search)
        loading(false)
      }, 300)
    },
    buildPayload() {
      return {
        filters: {
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          movie_ids: (this.filtersUi.movies || []).map((m) => m.id),
          territory: this.filters.territory || [],
          right: this.filters.right || [],
          language: (this.filtersUi.languages || []).map((l) => l.id),
          availability: this.filters.availability,
          acquired_as: this.filters.acquired_as,
          holdbacks: this.holdbackValue,
          holdback_territory: this.filters.holdback_territory || [],
          holdback_right: this.filters.holdback_right || [],
          holdback_start_date: this.filters.holdback_start_date,
          holdback_end_date: this.filters.holdback_end_date,
        },
        show_by: this.showBy?.length ? this.showBy : ['right', 'territory', 'language'],
        columns: this.selectedColumns,
        report_name: this.reportName || 'Rights Avails',
      }
    },
    resetAll() {
      this.clearFilters()
      this.resetOutputToDefaults()
      this.error = null
      this.columnSearch = ''
      this.activeGroup = 'all'
      this.filtersOpen = true
    },
    guard() {
      if (!this.hasDates) {
        this.$bvToast.toast('Set a From and To date first', {
          title: 'Rights Avails',
          variant: 'warning',
          solid: true,
        })
        this.goToStep('filters')
        return false
      }
      if (!this.outputColumns.length) {
        this.$bvToast.toast('Add at least one field to the report layout', {
          title: 'Rights Avails',
          variant: 'warning',
          solid: true,
        })
        this.goToStep('builder')
        return false
      }
      return true
    },
    async queueExport() {
      if (!this.guard()) return

      this.queueing = true
      this.error = null
      try {
        const res = await PostRightsAvailsExport(this.buildPayload())
        if (!res.data?.status) throw new Error(res.data?.message || 'Could not queue export')
        this.jobs = [res.data.data, ...this.jobs].slice(0, 20)
        this.filtersOpen = false
        this.startJobPolling()
        this.$nextTick(() => this.goToStep('results'))
        this.$bvToast.toast(
          'Export queued. It builds in the background — you can leave this page.',
          { title: 'Rights Avails', variant: 'success', solid: true },
        )
      } catch (e) {
        this.$bvToast.toast(e?.response?.data?.message || e.message || 'Could not queue export', {
          title: 'Rights Avails',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.queueing = false
      }
    },
    async loadJobs() {
      this.jobsLoading = true
      try {
        const res = await GetRightsAvailsExportJobs(20)
        if (res.data?.status) {
          this.jobs = res.data.data?.jobs || []
          this.error = null
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'Could not load exports'
      } finally {
        this.jobsLoading = false
      }
      // Polling only needs to run while something is actually in flight.
      if (this.activeJobCount) this.startJobPolling()
      else this.stopJobPolling()
    },
    startJobPolling() {
      if (this.jobsTimer) return
      this.jobsTimer = setInterval(() => this.loadJobs(), 4000)
    },
    stopJobPolling() {
      if (!this.jobsTimer) return
      clearInterval(this.jobsTimer)
      this.jobsTimer = null
    },
    async cancelJob(job) {
      try {
        await CancelRightsAvailsExportJob(job.job_id)
      } catch (e) {
        // the refresh below will show whatever the real state is
      }
      this.loadJobs()
    },
    requeueJob(job) {
      if (job.request) {
        this.$bvToast.toast('Re-queuing with the same settings', {
          title: 'Rights Avails',
          variant: 'info',
          solid: true,
        })
      }
      this.queueExport()
    },
    downloadUrl(job) {
      return getRightsAvailsDownloadUrl(job.file_name, job.report_name)
    },
    jobIcon(status) {
      return {
        queued: 'ClockIcon',
        running: 'LoaderIcon',
        done: 'CheckCircleIcon',
        failed: 'AlertCircleIcon',
        cancelled: 'SlashIcon',
      }[status] || 'CircleIcon'
    },
    jobStatusLabel(status) {
      return {
        queued: 'Queued',
        running: 'Building',
        done: 'Ready',
        failed: 'Failed',
        cancelled: 'Cancelled',
      }[status] || status
    },
    jobMeta(job) {
      const when = job.finished_at || job.started_at || job.queued_at
      const stamp = when ? moment(when).format('DD MMM, HH:mm') : ''
      if (job.status === 'done') {
        const rows = Number(job.row_count || 0).toLocaleString()
        return `${rows} rows · ${stamp}`
      }
      if (job.status === 'running') return `Started ${stamp}`
      if (job.status === 'queued') return `Queued ${stamp}`
      return stamp
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style scoped>
/* ── Design tokens ─────────────────────────────────────────────── */
.rb {
  --c-surface: #ffffff;
  --c-canvas: #f1f4f9;
  --c-line: #e2e8f0;
  --c-line-strong: #cbd5e1;
  --c-ink: #0f172a;
  --c-ink-soft: #475569;
  --c-muted: #64748b;
  --c-brand: #4338ca;
  --c-brand-soft: #eef2ff;
  --c-brand-line: #c7d2fe;
  --c-ok: #047857;
  --c-ok-bg: #d1fae5;
  --c-warn: #b45309;
  --c-warn-bg: #fef3c7;
  --c-block: #be123c;
  --c-block-bg: #ffe4e6;
  --r-sm: 0.375rem;
  --r-md: 0.5rem;
  --r-lg: 0.75rem;
  --sh-1: 0 1px 2px rgba(15, 23, 42, 0.05);
  --sh-2: 0 4px 16px rgba(15, 23, 42, 0.07);

  padding: 0 1.25rem 2.5rem;
  min-height: calc(100vh - 5rem);
  color: var(--c-ink);
  background: var(--c-canvas);
  font-variant-numeric: tabular-nums;
}

/* ── Toolbar ───────────────────────────────────────────────────── */
.rb-toolbar {
  position: sticky;
  top: 62px;
  z-index: 11;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  padding: 0.85rem 0;
  margin-bottom: 0.9rem;
  background: var(--c-canvas);
  border-bottom: 1px solid var(--c-line);
}
.rb-toolbar-id {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}
.rb-logo {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: var(--r-md);
  background: var(--c-brand);
  color: #fff;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow: var(--sh-1);
}
.rb-eyebrow {
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-muted);
}
.rb-name-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.rb-name {
  border: 1px solid transparent;
  background: transparent;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--c-ink);
  padding: 0.12rem 0.35rem;
  border-radius: var(--r-sm);
  width: 15ch;
  max-width: 100%;
  transition: all 0.15s ease;
}
.rb-name:hover { border-color: var(--c-line-strong); }
.rb-name:focus {
  outline: none;
  border-color: var(--c-brand);
  background: #fff;
  width: 22ch;
}
.rb-type-tag {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-brand);
  background: var(--c-brand-soft);
  border: 1px solid var(--c-brand-line);
  border-radius: 999px;
  padding: 0.12rem 0.5rem;
  white-space: nowrap;
}

.rb-steps {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin: 0 auto;
  background: #fff;
  border: 1px solid var(--c-line);
  border-radius: 999px;
  padding: 0.22rem;
  box-shadow: var(--sh-1);
}
.rb-step {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  background: transparent;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--c-muted);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.rb-step:hover { background: #f8fafc; color: var(--c-ink); }
.rb-step.active { background: var(--c-brand-soft); color: var(--c-brand); }
.rb-step-dot {
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  background: #e2e8f0;
  color: var(--c-ink-soft);
  font-size: 0.65rem;
  font-weight: 800;
  display: grid;
  place-items: center;
}
.rb-step.active .rb-step-dot { background: var(--c-brand); color: #fff; }
.rb-step.done .rb-step-dot { background: var(--c-ok); color: #fff; }
.rb-step.done { color: var(--c-ink); }

.rb-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}
.rb-btn { font-weight: 650; }
.rb-btn--run { box-shadow: var(--sh-1); }
.rb-menu-header { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; }
.rb-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.rb-menu-item-owner { margin-left: 0.35rem; color: var(--c-muted); font-size: 0.72rem; }
.rb-menu-item-del { color: var(--c-muted); }
.rb-menu-item-del:hover { color: var(--c-block); }

.rb-notice {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: var(--c-brand-soft);
  border: 1px solid var(--c-brand-line);
  color: #3730a3;
  border-radius: var(--r-md);
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  margin-bottom: 0.75rem;
}

/* ── Cards ─────────────────────────────────────────────────────── */
.rb-card {
  background: var(--c-surface);
  border: 1px solid var(--c-line);
  border-radius: var(--r-lg);
  margin-bottom: 0.85rem;
  box-shadow: var(--sh-1);
}
.rb-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.4rem 0.9rem 0.4rem 0.35rem;
}
.rb-card-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  background: transparent;
  padding: 0.6rem 0.55rem;
  color: var(--c-ink);
  cursor: pointer;
  border-radius: var(--r-md);
}
.rb-card-toggle:hover { background: #f8fafc; }
.rb-card-title { font-weight: 700; font-size: 0.92rem; }
.rb-link-btn {
  border: 0;
  background: transparent;
  color: var(--c-brand);
  font-size: 0.78rem;
  font-weight: 650;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: var(--r-sm);
}
.rb-link-btn:hover { background: var(--c-brand-soft); }
.rb-card-body { padding: 0 1rem 1.1rem; }

.rb-subsection { padding-top: 0.5rem; }
.rb-subsection + .rb-subsection {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--c-line);
}
.rb-subsection-head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.74rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-muted);
  margin-bottom: 0.65rem;
}
.rb-req {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--c-block);
  background: var(--c-block-bg);
  border-radius: 999px;
  padding: 0.08rem 0.4rem;
}

.rb-preset-row {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  margin-bottom: 0.7rem;
}
.rb-preset {
  border: 1px solid var(--c-line-strong);
  background: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--c-ink-soft);
  cursor: pointer;
  transition: all 0.15s ease;
}
.rb-preset:hover { border-color: var(--c-brand); color: var(--c-brand); }
.rb-preset.active {
  background: var(--c-brand);
  border-color: var(--c-brand);
  color: #fff;
}

.rb-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem 0.85rem;
  align-items: start;
}
.rb-nested {
  margin-top: 0.85rem;
  padding: 0.85rem;
  border: 1px dashed var(--c-line-strong);
  border-radius: var(--r-md);
  background: #f8fafc;
}
.rb-field { min-width: 0; }
.rb-field--span2 { grid-column: span 2; }
.rb-field--span4 { grid-column: span 4; }
.rb-label {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-ink-soft);
  margin-bottom: 0.3rem;
}
.rb-label-hint {
  font-size: 0.64rem;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: var(--c-muted);
}
.rb-input {
  width: 100%;
  font-size: 0.86rem;
  border-radius: var(--r-sm);
  border-color: var(--c-line-strong);
}
.rb-select { width: 100%; font-size: 0.86rem; }
.rb-select >>> .vs__dropdown-toggle,
.rb-field >>> .vue-treeselect__control {
  min-height: 38px;
  border-color: var(--c-line-strong);
  border-radius: var(--r-sm);
}
.rb-window-note {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  align-self: end;
  padding-bottom: 0.5rem;
  font-size: 0.78rem;
  color: var(--c-muted);
  line-height: 1.35;
}
.rb-help {
  margin: 0.35rem 0 0;
  font-size: 0.74rem;
  color: var(--c-muted);
  line-height: 1.35;
}

.rb-segmented {
  display: inline-flex;
  border: 1px solid var(--c-line-strong);
  border-radius: var(--r-sm);
  overflow: hidden;
  background: #fff;
  width: 100%;
}
.rb-seg {
  flex: 1;
  border: 0;
  background: transparent;
  padding: 0.5rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--c-ink-soft);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
}
.rb-seg + .rb-seg { border-left: 1px solid var(--c-line); }
.rb-seg:hover { background: #f8fafc; }
.rb-seg.active { background: var(--c-brand); color: #fff; }

.rb-optgrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}
.rb-opt {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  text-align: left;
  border: 1px solid var(--c-line-strong);
  background: #fff;
  border-radius: var(--r-md);
  padding: 0.55rem 0.7rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.rb-opt:hover { border-color: var(--c-brand); box-shadow: var(--sh-1); }
.rb-opt.active {
  border-color: var(--c-brand);
  background: var(--c-brand-soft);
  box-shadow: inset 0 0 0 1px var(--c-brand);
}
.rb-opt-name { font-size: 0.83rem; font-weight: 700; color: var(--c-ink); }
.rb-opt.active .rb-opt-name { color: var(--c-brand); }
.rb-opt-desc { font-size: 0.72rem; color: var(--c-muted); line-height: 1.3; }

.rb-combo-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0.75rem 0 0;
  padding: 0.5rem 0.7rem;
  border-radius: var(--r-md);
  background: var(--c-brand-soft);
  border: 1px solid var(--c-brand-line);
  color: #3730a3;
  font-size: 0.78rem;
}

.rb-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0 1rem 0.9rem;
  align-items: center;
}
.rb-chip-row--applied {
  padding: 0 0 0.85rem;
  border-bottom: 1px solid var(--c-line);
  margin-bottom: 0.85rem;
}
.rb-applied-label {
  font-size: 0.65rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--c-muted);
  margin-right: 0.15rem;
}
.rb-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.73rem;
  font-weight: 600;
  background: #f1f5f9;
  border: 1px solid var(--c-line);
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
  color: var(--c-ink-soft);
}
.rb-chip--removable { cursor: pointer; }
.rb-chip--removable:hover {
  border-color: var(--c-block);
  color: var(--c-block);
  background: var(--c-block-bg);
}
.rb-count {
  font-size: 0.7rem;
  font-weight: 750;
  color: var(--c-brand);
  background: var(--c-brand-soft);
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
}
.rb-count--solid { color: #fff; background: var(--c-brand); }

/* ── Builder ───────────────────────────────────────────────────── */
.rb-builder {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 0.85rem;
  margin-bottom: 0.85rem;
}
.rb-panel {
  background: var(--c-surface);
  border: 1px solid var(--c-line);
  border-radius: var(--r-lg);
  display: flex;
  flex-direction: column;
  box-shadow: var(--sh-1);
  overflow: hidden;
}
.rb-panel--layout { border-color: var(--c-brand-line); }
.rb-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem 0.6rem;
}
.rb-panel-head-main { min-width: 0; }
.rb-panel-head-actions { display: flex; gap: 0.35rem; flex-shrink: 0; }
.rb-panel-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-size: 0.92rem;
  font-weight: 750;
}
.rb-panel-hint {
  margin: 0.2rem 0 0;
  font-size: 0.74rem;
  color: var(--c-muted);
}
.rb-mini-btn { font-size: 0.74rem; font-weight: 650; padding: 0.22rem 0.55rem; }

.rb-panel-tools {
  padding: 0 0.95rem 0.65rem;
  border-bottom: 1px solid var(--c-line);
}
.rb-panel-tools--showby { background: #fafbff; padding-top: 0.7rem; }
.rb-search { position: relative; margin-bottom: 0.55rem; }
.rb-search-icon {
  position: absolute;
  left: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--c-muted);
}
.rb-search-input {
  width: 100%;
  border: 1px solid var(--c-line-strong);
  border-radius: var(--r-sm);
  padding: 0.42rem 2rem 0.42rem 1.95rem;
  font-size: 0.83rem;
  background: #fff;
}
.rb-search-input:focus {
  outline: none;
  border-color: var(--c-brand);
  box-shadow: 0 0 0 3px var(--c-brand-soft);
}
.rb-search-clear {
  position: absolute;
  right: 0.45rem;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: var(--c-muted);
  cursor: pointer;
  padding: 0.1rem;
  line-height: 1;
}
.rb-tabs { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.rb-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border: 1px solid var(--c-line);
  background: #fff;
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 650;
  color: var(--c-ink-soft);
  cursor: pointer;
  transition: all 0.15s ease;
}
.rb-tab span {
  font-size: 0.65rem;
  color: var(--c-muted);
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0 0.3rem;
}
.rb-tab:hover { border-color: var(--c-brand-line); }
.rb-tab.active { background: var(--c-ink); border-color: var(--c-ink); color: #fff; }
.rb-tab.active span { background: rgba(255, 255, 255, 0.18); color: #fff; }

.rb-showby { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.rb-showby-label {
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-ink-soft);
}
.rb-showby-chips { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.rb-toggle {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--c-line-strong);
  background: #fff;
  border-radius: 999px;
  padding: 0.22rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 650;
  color: var(--c-ink-soft);
  cursor: pointer;
  transition: all 0.15s ease;
}
.rb-toggle.active {
  background: var(--c-brand);
  border-color: var(--c-brand);
  color: #fff;
}

.rb-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 0.6rem 0.8rem 0.9rem;
  min-height: 240px;
  max-height: 380px;
}
.rb-scroll--layout { max-height: 420px; }
/* Auto-fits 2-3 fields per row depending on panel width, so a long library is
   scannable without scrolling through one item at a time. */
.rb-list {
  min-height: 140px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
  gap: 0.35rem;
  align-content: start;
}
/* Output rows carry an extra index badge, so they need a little more room. */
.rb-list--out { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
.rb-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.45rem;
  border: 1px solid var(--c-line);
  border-radius: var(--r-md);
  background: #fff;
  cursor: grab;
  user-select: none;
  min-width: 0;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
}
.rb-item:hover {
  border-color: var(--c-brand-line);
  box-shadow: var(--sh-2);
  transform: translateY(-1px);
}
.rb-item:focus-visible {
  outline: 2px solid var(--c-brand);
  outline-offset: 1px;
}
.rb-item--out { background: #fafbff; border-color: var(--c-brand-line); }
.rb-item-icon {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: var(--r-sm);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.rb-item-icon.is-avails { background: var(--c-brand-soft); color: var(--c-brand); }
.rb-item-icon.is-movie { background: #ecfeff; color: #0e7490; }
.rb-item-icon.is-crew { background: #fef3c7; color: #b45309; }
.rb-item-icon.is-art { background: #fce7f3; color: #be185d; }
.rb-idx {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: var(--r-sm);
  background: var(--c-brand);
  color: #fff;
  font-size: 0.64rem;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.rb-item-name {
  flex: 1;
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rb-item-action { color: var(--c-brand); flex-shrink: 0; opacity: 0; transition: opacity 0.15s ease; }
.rb-item:hover .rb-item-action { opacity: 1; }
.rb-item-remove {
  border: 0;
  background: transparent;
  color: #94a3b8;
  border-radius: var(--r-sm);
  padding: 0.15rem;
  cursor: pointer;
  line-height: 1;
  flex-shrink: 0;
}
.rb-item-remove:hover { color: var(--c-block); background: var(--c-block-bg); }

.rb-ghost {
  opacity: 0.5;
  background: var(--c-brand-soft) !important;
  border-style: dashed !important;
  border-color: var(--c-brand) !important;
}
.rb-chosen { box-shadow: var(--sh-2); }
.rb-dragging { cursor: grabbing; }

.rb-blank {
  text-align: center;
  color: var(--c-muted);
  font-size: 0.82rem;
  padding: 2rem 1rem;
}
.rb-blank--drop {
  border: 1.5px dashed var(--c-brand-line);
  border-radius: var(--r-md);
  background: #fafbff;
  color: var(--c-brand);
}
.rb-blank--drop small { color: var(--c-muted); }
.rb-inline-note {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-radius: var(--r-sm);
  background: var(--c-warn-bg);
  border: 1px solid #fde68a;
  color: var(--c-warn);
  font-size: 0.74rem;
  line-height: 1.35;
}

/* ── Results ───────────────────────────────────────────────────── */
.rb-results { padding: 0.9rem 1rem 1rem; }
.rb-results-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.8rem;
}
.rb-results-title { display: flex; align-items: center; gap: 0.6rem; }
.rb-results-tools { display: flex; align-items: center; gap: 0.4rem; }
.rb-icon-only { padding: 0.28rem 0.5rem; }

.rb-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 15rem;
  padding: 1.5rem;
  color: var(--c-muted);
}
.rb-state--error { color: var(--c-block); }
.rb-state-icon {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 999px;
  background: var(--c-brand-soft);
  color: var(--c-brand);
  display: grid;
  place-items: center;
  margin-bottom: 0.75rem;
}
.rb-state-title { font-size: 1.05rem; font-weight: 700; color: var(--c-ink); margin: 0 0 0.3rem; }
.rb-state-sub { max-width: 30rem; margin: 0 0 1rem; font-size: 0.86rem; }

@keyframes rb-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Fullscreen ────────────────────────────────────────────────── */

/* ── Responsive ────────────────────────────────────────────────── */
@media (max-width: 1280px) {
  .rb-steps { order: 3; width: 100%; margin: 0; justify-content: center; }
}
@media (max-width: 1100px) {
  .rb-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .rb-field--span4 { grid-column: span 2; }
  .rb-optgrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .rb-builder { grid-template-columns: 1fr; }
  .rb-scroll { max-height: 340px; }
}
/* ── Export jobs ───────────────────────────────────────── */
.rb-jobs {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
}
.rb-job {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.6rem 0.7rem;
  border: 1px solid var(--c-line);
  border-radius: 0.5rem;
  background: #fff;
  min-width: 0;
}
.rb-job--running { border-color: #bfdbfe; background: #f8fbff; }
.rb-job--done { border-color: #a7f3d0; }
.rb-job--failed { border-color: #fecdd3; background: #fff8f8; }
.rb-job--cancelled { opacity: 0.6; }
.rb-job-status {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  margin-bottom: 0.15rem;
}
.rb-job-status--queued { color: var(--c-muted); }
.rb-job-status--running { color: #2563eb; }
.rb-job-status--done { color: var(--c-ok); }
.rb-job-status--failed { color: var(--c-block); }
.rb-job-status--cancelled { color: var(--c-muted); }
.rb-job-name {
  font-size: 0.83rem;
  font-weight: 600;
  color: var(--c-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rb-job-meta { font-size: 0.71rem; color: var(--c-muted); }
.rb-job-error {
  font-size: 0.71rem;
  color: var(--c-block);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
/* Pushed to the bottom so cards in a row line up regardless of error text. */
.rb-job-actions { margin-top: auto; padding-top: 0.5rem; }
.rb-job-wait {
  display: block;
  font-size: 0.71rem;
  color: var(--c-muted);
  font-style: italic;
  text-align: center;
}
@media (max-width: 1400px) {
  .rb-jobs { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 1100px) {
  .rb-jobs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.rb-job-note {
  margin: 0.6rem 0 0;
  font-size: 0.74rem;
  color: var(--c-warn);
  display: flex;
  align-items: center;
}
.rb-spin { animation: rb-spin 1.1s linear infinite; }
@keyframes rb-spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .rb { padding: 0 0.75rem 2rem; }
  .rb-grid { grid-template-columns: 1fr; }
  .rb-field--span2, .rb-field--span4 { grid-column: span 1; }
  .rb-optgrid { grid-template-columns: 1fr; }
  .rb-toolbar-actions { width: 100%; justify-content: flex-end; }
  .rb-jobs { grid-template-columns: 1fr; }
}
</style>
